import type { FC } from 'react';
import { twMerge } from 'tailwind-merge';
import { useTheme, type DeepPartial } from 'flowbite-react';
import { mergeDeep } from '../helpers';
import { useDatePickerContext } from '../DatepickerContext';
import {
  addDays,
  getFirstDayOfTheMonth,
  getFormattedDate,
  getWeekDays,
  isDateEqual,
  isDateInRange,
} from '../helpers';

export interface FlowbiteDatepickerViewsDaysTheme {
  header: {
    base: string;
    title: string;
  };
  items: {
    base: string;
    item: {
      base: string;
      selected: string;
      disabled: string;
      inRange: string;
    };
  };
}

export interface DatepickerViewsDaysProps {
  theme?: DeepPartial<FlowbiteDatepickerViewsDaysTheme>;
}

export const DatepickerViewsDays: FC<DatepickerViewsDaysProps> = ({
  theme: customTheme = {},
}) => {
  const theme = mergeDeep(useTheme().theme.datepicker.views.days, customTheme);

  const {
    weekStart,
    minDate,
    maxDate,
    rangeStart,
    rangeEnd,
    viewDate,
    selectedDate,
    changeSelectedDate,
    language,
  } = useDatePickerContext();

  const weekDays = getWeekDays(language, weekStart);
  const startDate = getFirstDayOfTheMonth(viewDate, weekStart);

  return (
    <>
      <div className={theme.header.base}>
        {weekDays.map((day, index) => (
          <span key={index} className={theme.header.title}>
            {day}
          </span>
        ))}
      </div>
      <div className={theme.items.base}>
        {[...Array(42)].map((_date, index) => {
          const currentDate = addDays(startDate, index - 1);
          const day = getFormattedDate(language, currentDate, {
            day: 'numeric',
          });

          const isSelected = isDateEqual(selectedDate, currentDate);
          const isDisabled = !isDateInRange(currentDate, minDate, maxDate);
          const isInRange = isDateInRange(currentDate, rangeStart, rangeEnd);

          return (
            <button
              disabled={isDisabled}
              key={index}
              type="button"
              className={twMerge(
                theme.items.item.base,
                isInRange && theme.items.item.inRange,
                isSelected && theme.items.item.selected,
                isDisabled && theme.items.item.disabled
              )}
              onClick={() => {
                if (isDisabled) return;

                changeSelectedDate(currentDate, true);
              }}
            >
              {day}
            </button>
          );
        })}
      </div>
    </>
  );
};
